import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnInit,
  ViewChild,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CdkConnectedOverlay, ConnectedOverlayPositionChange, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';

import { SoGroupedSelectData, SoGroupedSelectItem, SoGroupedSelectKey } from '../interfaces/grouped-select.interfaces';

interface SoGroupedSelectGroup {
  label: string;
  options: SoGroupedSelectItem[];
}

interface SoGroupedSelectRenderRow {
  rowType: 'header' | 'option';
  id: string;
  label: string;
  item: SoGroupedSelectItem | null;
  flatIndex: number;
}

@Component({
  selector: 'so-grouped-select-ui',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, OverlayModule],
  templateUrl: './so-grouped-select.component.html',
  styleUrl: './so-grouped-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoGroupedSelectUI),
      multi: true,
    },
  ],
})
export class SoGroupedSelectUI implements ControlValueAccessor, OnInit {
  // Grouped data inputs
  groups = input<SoGroupedSelectData>({});
  items = input<SoGroupedSelectData>({});
  disabledKeys = input<SoGroupedSelectKey[]>([]);

  // Visual inputs
  label = input<string>('');
  selectLabel = input<string>('');
  placeholder = input<string>('Seleccionar una opcion');
  customStyle = input<string>('');
  labelStyle = input<string>('');
  displayValueStyle = input<string>('');
  displayValueClass = input<string>('');
  selectHeight = input<number>(36);
  display = input<'vertical' | 'horizontal'>('vertical');
  tooltipDisabledText = input<string>('');

  // Behavior inputs
  disabled = input<boolean>(false);
  isDisabled = input<boolean>(false);
  loading = input<boolean>(false);
  isLoading = input<boolean>(false);
  required = input<boolean>(false);
  requiredMark = input<boolean>(false);
  addSelectOption = input<boolean>(false);
  isFilter = input<boolean>(false);
  emptyMessage = input<string>('No hay opciones disponibles');
  noResultsMessage = input<string>('No encontramos resultados');
  selectedItemId = input<SoGroupedSelectKey>(null);
  formControl = input<FormControl | null>(null);

  // Outputs
  selectionChange = output<SoGroupedSelectKey>();
  selectionText = output<string>();

  @ViewChild(CdkConnectedOverlay) connectedOverlay?: CdkConnectedOverlay;
  @ViewChild('triggerRef') triggerRef?: ElementRef<HTMLElement>;

  protected internalValue = signal<SoGroupedSelectKey>(null);
  protected isOpen = signal<boolean>(false);
  protected activeIndex = signal<number>(-1);
  protected triggerWidth = signal<number>(0);
  protected isAbove = signal<boolean>(false);

  private cvaDisabled = signal<boolean>(false);
  private lastExternalSelectedItemId = signal<string | null>(null);

  protected tempSingleValue = signal<SoGroupedSelectKey>(null);
  protected searchTerm = signal<string>('');

  readonly panelId = `so-grouped-select-panel-${Math.random().toString(36).slice(2)}`;
  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  private onChange: (value: SoGroupedSelectKey) => void = () => {};
  private onTouched: () => void = () => {};

  protected effectiveLabel = computed(() => this.label() || this.selectLabel());
  protected effectiveDisabled = computed(() => this.disabled() || this.isDisabled() || this.cvaDisabled());
  protected effectiveLoading = computed(() => this.loading() || this.isLoading());
  private injector = inject(Injector);
  private controlRequired = signal(false);
  protected effectiveRequired = computed(() => this.required() || this.requiredMark() || this.controlRequired());

  protected groupedOptions = computed<SoGroupedSelectGroup[]>(() => {
    const source = this.resolveGroups();
    const rows: SoGroupedSelectGroup[] = [];

    if (this.addSelectOption()) {
      rows.push({
        label: '',
        options: [{ key: null, value: 'Seleccionar' }],
      });
    }

    for (const [groupLabel, options] of Object.entries(source)) {
      if (!Array.isArray(options) || options.length === 0) continue;
      rows.push({
        label: groupLabel,
        options,
      });
    }

    return rows;
  });

  protected renderRows = computed<SoGroupedSelectRenderRow[]>(() => {
    const rows: SoGroupedSelectRenderRow[] = [];
    let headerIndex = 0;
    let optionIndex = 0;

    for (const group of this.groupedOptions()) {
      if (group.label) {
        rows.push({
          rowType: 'header',
          id: `header-${headerIndex}`,
          label: group.label,
          item: null,
          flatIndex: -1,
        });
        headerIndex++;
      }

      for (const option of group.options) {
        rows.push({
          rowType: 'option',
          id: `option-${optionIndex}`,
          label: option.display ?? option.value,
          item: option,
          flatIndex: optionIndex,
        });
        optionIndex++;
      }
    }

    return rows;
  });

  protected flatOptionRows = computed(() => this.renderRows().filter(row => row.rowType === 'option' && !!row.item));

  protected filteredRenderRows = computed<SoGroupedSelectRenderRow[]>(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) return this.renderRows();
    const rows = this.renderRows();
    const result: SoGroupedSelectRenderRow[] = [];
    let pendingHeader: SoGroupedSelectRenderRow | null = null;
    for (const row of rows) {
      if (row.rowType === 'header') {
        pendingHeader = row;
      } else {
        const label = (row.item?.display ?? row.item?.value ?? '').toLowerCase();
        if (label.includes(term)) {
          if (pendingHeader) {
            result.push(pendingHeader);
            pendingHeader = null;
          }
          result.push(row);
        }
      }
    }
    return result;
  });

  protected filteredOptionRows = computed(() => this.filteredRenderRows().filter(row => row.rowType === 'option' && !!row.item));
  protected hasNoItems = computed(() => this.flatOptionRows().length === 0);
  protected hasNoSearchResults = computed(() => !this.hasNoItems() && this.filteredOptionRows().length === 0);

  protected currentDisplayItem = computed(() => {
    const normalized = this.normalizeKey(this.internalValue());
    if (normalized === null) return null;

    const row = this.flatOptionRows().find(item => this.normalizeKey(item.item?.key ?? null) === normalized);
    return row?.item?.display ?? row?.item?.value ?? null;
  });

  protected activeOptionId = computed(() => {
    const activeRow = this.activeOptionRows()[this.activeIndex()];
    return activeRow ? this.getOptionId(activeRow.flatIndex) : null;
  });

  ngOnInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    queueMicrotask(() => {
      const control = ngControl?.control;
      if (control?.hasValidator) {
        this.controlRequired.set(control.hasValidator(Validators.required));
      }
    });
  }

  constructor() {
    effect(() => {
      this.groupedOptions();

      const external = this.selectedItemId();
      const normalizedExternal = this.normalizeKey(external);
      if (normalizedExternal === this.lastExternalSelectedItemId()) return;

      this.lastExternalSelectedItemId.set(normalizedExternal);
      this.internalValue.set(external);
      this.ensureActiveIndex();
    }, {  });

    effect(() => {
      this.renderRows();
      this.internalValue();
      this.ensureActiveIndex();
    }, {  });
  }

  // ControlValueAccessor
  writeValue(value: SoGroupedSelectKey): void {
    this.internalValue.set(value);
    this.ensureActiveIndex();
  }

  registerOnChange(fn: (value: SoGroupedSelectKey) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
  }

  // Public API
  normalizeKey(key: SoGroupedSelectKey): string | null {
    if (key === null || key === undefined) return null;
    return String(key);
  }

  shouldBeDisabled(key: SoGroupedSelectKey, itemDisabled = false): boolean {
    if (itemDisabled) return true;
    return this.disabledKeys().some(disabledKey => this.normalizeKey(disabledKey) === this.normalizeKey(key));
  }

  getOptionId(index: number): string {
    return `${this.panelId}-option-${index}`;
  }

  trackByRow(_index: number, row: SoGroupedSelectRenderRow): string {
    return row.id;
  }

  isOptionRowSelected(row: SoGroupedSelectRenderRow): boolean {
    if (row.rowType !== 'option' || !row.item) return false;
    return this.normalizeKey(row.item.key) === this.normalizeKey(this.internalValue());
  }

  isOptionRowDisabled(row: SoGroupedSelectRenderRow): boolean {
    if (row.rowType !== 'option' || !row.item) return true;
    return this.shouldBeDisabled(row.item.key, !!row.item.disabled);
  }

  selectRow(row: SoGroupedSelectRenderRow): void {
    if (row.rowType !== 'option' || !row.item) return;
    this.selectItem(row.item);
  }

  // Panel behavior
  openPanel(): void {
    if (this.isOpen() || this.effectiveDisabled()) return;
    this.setTriggerWidth();
    this.ensureActiveIndex();
    this.isOpen.set(true);
    queueMicrotask(() => {
      untracked(() => this.scrollToActiveOption());
      this.connectedOverlay?.overlayRef?.updatePosition();
    });
  }

  closePanel(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
  }

  openPanelSingleFilter(): void {
    if (this.isOpen() || this.effectiveDisabled()) return;
    this.tempSingleValue.set(this.internalValue());
    this.searchTerm.set('');
    this.isOpen.set(true);
    this.setTriggerWidth();
    this.ensureActiveIndex();
  }

  selectTempSingleItem(row: SoGroupedSelectRenderRow): void {
    if (row.rowType !== 'option' || !row.item) return;
    if (this.isOptionRowDisabled(row)) return;
    const currentTemp = this.normalizeKey(this.tempSingleValue());
    const itemKey = this.normalizeKey(row.item.key);
    this.tempSingleValue.set(currentTemp === itemKey ? null : row.item.key);
  }

  isTempSingleSelected(key: SoGroupedSelectKey): boolean {
    return this.normalizeKey(this.tempSingleValue()) === this.normalizeKey(key);
  }

  applySingleSelection(): void {
    const selectedKey = this.tempSingleValue();
    this.internalValue.set(selectedKey);
    this.onChange(selectedKey);
    this.onTouched();
    this.selectionChange.emit(selectedKey);
    const row = this.flatOptionRows().find(r => this.normalizeKey(r.item?.key ?? null) === this.normalizeKey(selectedKey));
    if (row?.item) {
      this.selectionText.emit(row.item.display ?? row.item.value);
    }
    this.closePanel();
  }

  cancelSingleSelection(): void {
    this.tempSingleValue.set(this.internalValue());
    this.searchTerm.set('');
    this.closePanel();
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.ensureActiveIndex();
  }

  isActiveOptionRow(row: SoGroupedSelectRenderRow): boolean {
    return this.activeOptionRows()[this.activeIndex()]?.id === row.id;
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', 'Escape'].includes(event.key)) return;
    this.onFilterTriggerKeydown(event);
  }

  onTriggerClick(): void {
    if (this.effectiveDisabled()) return;
    this.isOpen() ? this.closePanel() : this.openPanel();
  }

  onFilterTriggerKeydown(event: KeyboardEvent): void {
    if (this.effectiveDisabled()) return;

    switch (event.key) {
      case 'Tab':
        this.cancelSingleSelection();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen()) this.openPanelSingleFilter();
        this.moveActiveIndex(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) this.openPanelSingleFilter();
        this.moveActiveIndex(-1);
        break;
      case 'Home':
        event.preventDefault();
        if (!this.isOpen()) this.openPanelSingleFilter();
        this.setBoundaryActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        if (!this.isOpen()) this.openPanelSingleFilter();
        this.setBoundaryActiveIndex(-1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.isOpen()) {
          this.openPanelSingleFilter();
        } else if (this.activeIndex() >= 0) {
          const row = this.activeOptionRows()[this.activeIndex()];
          if (row) this.selectTempSingleItem(row);
        }
        break;
      case 'Escape':
        if (this.isOpen()) {
          event.preventDefault();
          this.cancelSingleSelection();
        }
        break;
    }
  }

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.effectiveDisabled()) return;

    switch (event.key) {
      case 'Tab':
        this.closePanel();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen()) this.openPanel();
        this.moveActiveIndex(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) this.openPanel();
        this.moveActiveIndex(-1);
        break;
      case 'Home':
        event.preventDefault();
        if (!this.isOpen()) this.openPanel();
        this.setBoundaryActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        if (!this.isOpen()) this.openPanel();
        this.setBoundaryActiveIndex(-1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.isOpen()) {
          this.openPanel();
        } else if (this.activeIndex() >= 0) {
          this.selectByActiveIndex();
        }
        break;
      case 'Escape':
        if (this.isOpen()) {
          event.preventDefault();
          this.closePanel();
        }
        break;
    }
  }

  selectItem(item: SoGroupedSelectItem): void {
    if (this.shouldBeDisabled(item.key, !!item.disabled)) return;

    this.internalValue.set(item.key);
    this.onChange(item.key);
    this.onTouched();
    this.selectionChange.emit(item.key);
    this.selectionText.emit(item.display ?? item.value);
    this.closePanel();
  }

  onPositionChange(event: ConnectedOverlayPositionChange): void {
    this.isAbove.set(event.connectionPair.overlayY === 'bottom');
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isOpen()) return;
    this.setTriggerWidth();
    this.connectedOverlay?.overlayRef?.updatePosition();
  }

  private resolveGroups(): SoGroupedSelectData {
    const grouped = this.groups();
    if (grouped && Object.keys(grouped).length > 0) return grouped;
    return this.items();
  }

  private setTriggerWidth(): void {
    this.triggerWidth.set(this.triggerRef?.nativeElement.getBoundingClientRect().width ?? 0);
  }

  private selectByActiveIndex(): void {
    const row = this.activeOptionRows()[this.activeIndex()];
    if (!row?.item) return;
    this.selectItem(row.item);
  }

  private moveActiveIndex(delta: number): void {
    const options = this.activeOptionRows();
    if (!options.length) {
      this.activeIndex.set(-1);
      return;
    }

    const firstEnabled = options.findIndex(row => !this.isOptionRowDisabled(row));
    if (firstEnabled === -1) {
      this.activeIndex.set(-1);
      return;
    }

    let nextIndex = this.activeIndex();
    let attempts = 0;

    do {
      attempts++;
      nextIndex += delta;
      if (nextIndex < 0) nextIndex = options.length - 1;
      if (nextIndex >= options.length) nextIndex = 0;
    } while (attempts <= options.length && this.isOptionRowDisabled(options[nextIndex]));

    if (this.isOptionRowDisabled(options[nextIndex])) {
      this.activeIndex.set(-1);
      return;
    }

    this.activeIndex.set(nextIndex);
    this.scrollToActiveOption();
  }

  private ensureActiveIndex(): void {
    const options = this.activeOptionRows();
    if (!options.length) {
      this.activeIndex.set(-1);
      return;
    }

    const normalizedValue = this.normalizeKey(this.internalValue());
    if (normalizedValue === null) {
      this.activeIndex.set(this.findFirstEnabledIndex(options));
      untracked(() => this.scrollToActiveOption());
      return;
    }

    const foundIndex = options.findIndex(row => this.normalizeKey(row.item?.key ?? null) === normalizedValue);
    if (foundIndex !== -1 && !this.isOptionRowDisabled(options[foundIndex])) {
      this.activeIndex.set(foundIndex);
    } else {
      this.activeIndex.set(this.findFirstEnabledIndex(options));
    }

    untracked(() => this.scrollToActiveOption());
  }

  private findFirstEnabledIndex(options: SoGroupedSelectRenderRow[]): number {
    return options.findIndex(row => !this.isOptionRowDisabled(row));
  }

  onPanelKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    if (this.isFilter()) this.cancelSingleSelection();
    else this.closePanel();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isOpen() || event.defaultPrevented) return;
    if (this.isFilter()) this.onFilterTriggerKeydown(event);
    else this.onTriggerKeydown(event);
  }

  private activeOptionRows(): SoGroupedSelectRenderRow[] {
    return this.isFilter() ? this.filteredOptionRows() : this.flatOptionRows();
  }

  private setBoundaryActiveIndex(direction: 0 | -1): void {
    const options = this.activeOptionRows();
    const indexes = direction === 0 ? options.map((_, index) => index) : options.map((_, index) => options.length - index - 1);
    const next = indexes.find(index => !this.isOptionRowDisabled(options[index]));
    this.activeIndex.set(next ?? -1);
    untracked(() => this.scrollToActiveOption());
  }

  private scrollToActiveOption(): void {
    if (!this.isOpen() || this.activeIndex() < 0) return;

    queueMicrotask(() => {
      const optionId = this.getOptionId(this.activeOptionRows()[this.activeIndex()]?.flatIndex ?? -1);
      const optionElement = document.getElementById(optionId);
      optionElement?.scrollIntoView({ block: 'nearest' });
    });
  }
}
